import _ from 'lodash';
import moment from 'moment';
import { statusIndicatorFilterOptions } from '@rs-core/fhir/resource/columnMapping/consts/columns';
import generatePillDateFormat from '@worklist-2/ui/src/views/utils/generatePillDateFormat';
import axios from 'axios';

export const wheelMenus = (t, permissions, proactEnableVisitOnOmegaDial = false, proactEnableBilling = false) => {
	return [
		{
			label: t('workList:study'),
			icon: 'study',
			visible: permissions?.study?.read,
		},
		{
			label: t('workList:documentViewer'),
			icon: 'documentviewer',
			visible: permissions?.['document viewer']?.read,
		},
		{
			label: t('workList:imageViewer'),
			icon: 'imageviewer',
			visible: permissions?.['image viewer']?.read,
		},
		{
			label: t('workList:studyHistory'),
			icon: 'studyexplorer',
			visible: permissions?.['study history']?.read,
		},
		{
			label: t('workList:send'),
			icon: 'sendstudy',
			visible: permissions?.send?.read,
		},
		{
			label: t('workList:billing'),
			icon: 'billing',
			visible: proactEnableBilling, // need UAC
		},
		{
			label: t('workList:patient'),
			icon: 'patient',
			visible: permissions?.patient?.read,
		},
		{
			label: t('workList:visit'),
			icon: 'visit',
			visible: proactEnableVisitOnOmegaDial && permissions?.visit?.read,
		},
		{
			label: t('workList:order'),
			icon: 'order',
			visible: permissions?.order?.read,
		},
	];
};

// store filter temporarily
export const updateTemporaryFilters = newColumnArray => {
	const newFilterArray = [];
	_.map(newColumnArray, element => {
		if (
			(_.isString(element.filter) && !_.isEmpty(element.filter)) ||
			(_.isArray(element.filter) && element.filter.filter(str => str !== '').length > 0)
		) {
			let updatedValues = [];
			if (element.filterType === 'multi-select' || element.filterType === 'date-range') {
				updatedValues = element.filter;
			} else {
				updatedValues = [element.filter];
			}
			newFilterArray.push({
				label: element.label,
				columnIdentity: element.name,
				values: updatedValues,
				displayValue: element?.displayValue ? element.displayValue : updatedValues,
				capsuleMenuItems: element.capsuleMenuItems,
				filterType: element.filterType,
				toolTip: element.toolTip,
				valueSet: element.valueSet,
				searchParameter: element.searchParameter,
				searchValueSet: element.valueSet,
			});
		}
	});

	return newFilterArray;
};

export const updateTemporarySort = newColumnArray => {
	const newSort = [];

	_.map(newColumnArray, column => {
		if (column.sort && column.sort !== 'none') {
			newSort.push({
				sort: column.sort === 'asc' ? column.name : `-${column.name}`,
				sortOrder: column.sortOrder,
			});
		}
	});

	return _.orderBy(newSort, ['sortOrder'], ['asc']).map(sortObj => sortObj.sort);
};

export const isValidGUID = guid => {
	const guidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
	return guidPattern.test(guid);
};

/**
 * Get the columns for settings
 * @param {*} columnList
 * @param {*} filters
 * @param {*} sort
 * @returns
 */
export const getColumnsForSettings = (columnList, filters, sort) => {
	_.remove(columnList, col => col.name === 'id' || col.name === 'flagColTF');
	return columnList?.map(cl => {
		const filter = filters?.find(f => f.columnIdentity === cl.name);
		const sortIndex = _.findIndex(sort, sortValue => cl.name === sortValue || cl.name === sortValue.slice(1));
		let sortFinalValue = sortIndex !== -1 ? sort[sortIndex] : null;
		if (sortFinalValue) {
			sortFinalValue = sortFinalValue?.charAt(0) === '-' ? 'desc' : 'asc';
		}
		return {
			...cl,
			filter: filter?.actualValues ? filter.actualValues : filter?.values,
			displayValue: filter?.displayValue ? filter.displayValue : filter?.values,
			sortOrder: sortIndex !== -1 ? sortIndex : null,
			sort: sortFinalValue,
		};
	});
};

export const getStatusIndicatorOptions = (statusIndicators, filterOptions = statusIndicatorFilterOptions) => {
	const preAuthConfig = statusIndicators?.find(x => x.name === 'Pre-auth');
	const eligibilityConfig = statusIndicators?.find(x => x.name === 'Eligibility');
	const includePreAuthStatus = preAuthConfig ? preAuthConfig.checked : true;
	const includeEligibilityStatus = eligibilityConfig ? eligibilityConfig.checked : true;
	const includeChargeConfig = statusIndicators?.find(x => x.name === 'Charge-posting');
	const includeChargeStatus = includeChargeConfig ? includeChargeConfig.checked : false;
	return filterOptions.filter(filterOption => {
		if (filterOption.id.startsWith('eligibility')) {
			return includeEligibilityStatus;
		}
		if (filterOption.id.startsWith('prior')) {
			return includePreAuthStatus;
		}
		if (filterOption.id.startsWith('charge')) {
			return includeChargeStatus;
		}

		return true;
	});
};

/**
 * Check if the filter value is in the check value
 * @param {*} filterValue Filtering value
 * @param {*} checkValue Checking value
 * @returns
 */
export const isMultiMatchWord = (filterValue, checkValue) => {
	const checkValues = checkValue.split(' ');
	const filterValues = filterValue.split(' ');

	return filterValues.every(fv => checkValues.some(cv => cv.toLowerCase().includes(fv.toLowerCase())));
};

/**
 * Check all filters for the data locally
 * @param {*} studyRes
 * @param {*} filters
 * @param {*} columnMapping
 * @returns
 */
export const localFiltering = (studyRes, filters, columnMapping) => {
	if (filters && !_.isEmpty(filters)) {
		let matched = true;
		let filteredStudyRes = studyRes;

		for (const element of filters) {
			if (!_.isEmpty(element)) {
				matched = checkMatchedFilter(columnMapping, element, filteredStudyRes, matched);

				if (!matched) {
					filteredStudyRes = null;
					break;
				}
			}
		}

		return filteredStudyRes;
	}

	return studyRes;
};

/**
 * Check if the study is within the time window
 * @param {*} datetimeUtc
 * @returns
 */
export const isWithinTimeWindow = datetimeUtc => {
	const studyDateTime = new Date(datetimeUtc);

	if (isNaN(studyDateTime)) {
		return false;
	}

	// Get the current UTC time
	const currentDateTime = moment(new Date().toLocaleString('en-US', { timeZone: 'UTC' }));

	// Calculate the time difference in milliseconds
	const timeDiff = currentDateTime.diff(studyDateTime, 'milliseconds');

	// We can always change the time window if needed
	return timeDiff <= 60000;
};

/**
 * Check if the study data matches the filter
 * @param {*} columnMapping
 * @param {*} element
 * @param {*} studyRes
 * @param {*} matched
 * @returns
 */
const checkMatchedFilter = (columnMapping, element, studyRes, matched) => {
	const mappingField = element.columnIdentity ? element.columnIdentity : element.label;
	const fieldMapping = columnMapping[mappingField];
	const getValueFn = fieldMapping?.accessorFn ? fieldMapping?.accessorFn : fieldMapping?.getDisplay;
	const val = !!getValueFn && getValueFn(studyRes);

	const filterType = fieldMapping?.filterType ? fieldMapping.filterType : 'none';
	const queryType = fieldMapping?.queryType;

	switch (filterType) {
		case 'suggest':
			matched =
				val?.toLowerCase().includes(element.displayValue.toLowerCase()) ||
				studyRes[element.searchParameter] == element.values;
			break;
		case 'text-search':
			if (queryType === 'multi-match') {
				matched = isMultiMatchWord(element.values, val);
			} else {
				matched = val?.toLowerCase().includes(element.values.toLowerCase());
			}
			break;
		case 'single-select':
			matched = val === element.values[0];
			break;
		case 'multi-select':
		case 'checkbox-multi-select':
			matched = element.values.includes(val);
			break;
		case 'date-time':
			matched =
				moment(element.values).format('YYYY-MM-DDTHH:mm:ss') === moment(val).format('YYYY-MM-DDTHH:mm:ss');
			break;
		case 'date-range':
			if (_.isArray(element.values)) {
				matched =
					moment(element.values[0]).format('YYYY-MM-DDTHH:mm:ss') <=
						moment(val).format('YYYY-MM-DDTHH:mm:ss') &&
					moment(val).format('YYYY-MM-DDTHH:mm:ss') <=
						moment(element.values[1]).format('YYYY-MM-DDTHH:mm:ss');
			} else {
				const pillDateRangeValue = generatePillDateFormat(element.values);
				matched =
					moment(pillDateRangeValue[0]).format('YYYY-MM-DDTHH:mm:ss') <=
						moment(val).format('YYYY-MM-DDTHH:mm:ss') &&
					moment(val).format('YYYY-MM-DDTHH:mm:ss') <=
						moment(pillDateRangeValue[1]).format('YYYY-MM-DDTHH:mm:ss');
			}
			break;
		default:
			matched = val.toLowerCase() === element.values.toLowerCase();
			break;
	}
	return matched; // Return the new variable
};

export const bulidFilterForGroup = (columnProps, rowData, groupItem) => {
	let filterItem = {};

	if (!columnProps) {
		return filterItem;
	}

	const { filterType, column, searchParameter, groupSearchParameter, type } = columnProps;
	let filterValue = rowData[column]?.id || rowData[column]?.value;
	let groupInterval = groupItem?.groupInterval;

	if (!columnProps.options?.isTimeNumber && (['date-range', 'date-time'].includes(filterType) || type === 'time')) {
		if (!['year', 'month', 'day'].includes(groupInterval)) {
			groupInterval = 'day';
		}

		const date = moment(filterValue);
		const startOf = date.clone().startOf(groupInterval);
		const endOf = date.clone().endOf(groupInterval);

		filterItem = {
			columnIdentity: column,
			values: [startOf.toDate(), endOf.toDate()],
			searchParameter: groupSearchParameter || searchParameter,
			groupFilterType: 'date-range',
			bypassConvert: true,
		};
	} else {
		const isExact = columnProps.type === 'string-array' || columnProps.options?.isGroupExactMatch;

		let values = filterValue;

		if (columnProps.options?.isArrayValue) {
			try {
				values = JSON.parse(filterValue);
			} catch (e) {
				values = filterValue.split('|');
			}
		}

		filterItem = {
			columnIdentity: column,
			values: values,
			searchParameter: `${groupSearchParameter || searchParameter}${isExact ? ':exact' : ''}`,
			bypassConvert: true,
		};
	}

	return filterItem;
};

export const getDefaultOperator = columnMapping => {
	let operator = '';
	let columnType = columnMapping?.type;
	let filterType = columnMapping?.filterType;
	switch (columnType) {
		case 'time':
			operator = 'inDateRange';
			break;
		case 'string':
			operator = 'equals';
			if (filterType === 'text-search') {
				operator = 'contains';
			}
			break;
		case 'string-array':
			operator = 'contains';
			break;
		case 'number':
			operator = 'equals';
			break;
		default:
			operator = 'contains';
			break;
	}
	return operator;
};

export const getItemStyles = currentOffset => {
	if (!currentOffset) {
		return { display: 'none' };
	}

	const { x, y } = currentOffset;
	const transform = `translate(${x}px, ${y}px)`;
	return {
		transform,
		WebkitTransform: transform,
	};
};

const loadData = (uniqueMasterOrg, __config) => {
	const promiseArr = [];
	uniqueMasterOrg.map(org => {
		promiseArr.push(
			axios.get(
				`${__config.data_sources.fhir}/StudyStatus/?_dc=${new Date().getTime()}&_summary=false&organizationid=${
					org.internalMasterOrganizationID
				}&page=1&start=0&_sort=statusValue`,
				{
					headers: {
						Accept: '*/*',
					},
				}
			)
		);
	});

	return Promise.all(promiseArr).then(response => {
		let dataArray = [];

		response.forEach(res => {
			dataArray = dataArray.concat(res.data.entry);
		});

		return dataArray;
	});
};

export const getStatusColorCode = async (gridData, __config) => {
	const uniqueMasterOrg = Array.from(
		new Map(gridData.map(data => [data.internalMasterOrganizationID, data])).values()
	);

	const statusData = await loadData(uniqueMasterOrg, __config);

	// Process the arrays to add color where conditions match
	const processedArray = gridData.map(item1 => {
		const matchingItem = statusData.find(item2 => {
			const { resource } = item2;

			const orgMatch = item1.internalMasterOrganizationID === resource.organization.id;

			// Find studyStatus extension
			const studyStatusExtension = item1.extension.find(
				ext => ext.url === 'http://www.ramsoft.com/fhir/StructureDefinition/studyStatus'
			);

			// Check status match if studyStatus extension exists
			const statusMatch = studyStatusExtension
				? studyStatusExtension.valueCoding.display === resource.status
				: false;

			return orgMatch && statusMatch;
		});

		if (matchingItem) {
			return {
				...item1,
				studyStatusColor: matchingItem.resource.color,
				matchedStatus: matchingItem.resource.status,
			};
		}

		return item1;
	});
	return processedArray;
};

/**
 * Use recursive function to reach the last level of the group
 * @param {*} row Group row
 * @param {*} groupLevel Current group level
 * @param {*} recordInfo Record information needed to find the last group
 * @param {*} groupingItems Grouping items setting
 * @returns {}
 * - groupRow: The group row that contains the record
 * - subRowIndex: The index of the record in the group row
 * - groupLevel: The level of the group row
 */
export const getLastGroupByContentRecord = (row, groupLevel, recordInfo, groupingItems) => {
	// Check current group level if it is the last level
	if (row.subRows && row.subRows.length > 0 ) {
		if (groupLevel === groupingItems.length - 1) {
			// If the row is in the removeList, remove it
			const findIndex = row.subRows.findIndex(sRow => sRow.id == recordInfo.id);
			if (findIndex > -1) {
				return {
					groupRow: row,
					subRowIndex: findIndex,
					groupLevel,
				};
			}

			return null;
		}
		
		// Check the next level of the group
		for (const subRow of row.subRows) {
			if (subRow.id !== recordInfo.bypassGroupID) {
				// Recursively check the next level of the group
				const found = getLastGroupByContentRecord(subRow, groupLevel + 1, recordInfo, groupingItems);
				if (found) {
					return found;
				}
			}
		}
	}
	return null;
};

/**
 * Get the group content based on the group name and column properties
 * @param {Object} row The row object containing the data
 * @param {string} groupName The name of the group
 * @param {Object} columnProps The column properties object
 * @return {string} The display value for the group content
 */
export const getGroupContent = (row, groupName, groupType) => {
	const groupData = row.rawData[groupName];
	let display = '';

	if (groupData) {
		display = groupData.value;

		if (groupType === 'string-array') {
			display = display?.replaceAll('|', ' | ');
		}
	}

	return display;
};

/**
 * Compare the group content with the study row value
 * @param {Object} groupRow The group row object
 * @param {Object} studyRow The study row object
 * @param {Object} columnProps The column properties object
 * @return {number} The comparison result: -1, 0, or 1
 */
export const compareGroupContent = (groupRow, studyRow, columnProps) => {
	const groupType = columnProps.type;
	let groupValue = getGroupContent(groupRow, columnProps.column, groupType);
	let studyValue = columnProps.getOrignialValue ? columnProps.getOrignialValue(studyRow) : columnProps.getDisplay(studyRow);

	// Handle null and empty values
    if (!groupValue && !studyValue) {
        return 0; // Both are null or empty, considered equal
    }
	if (!groupValue) {
        return -1; // Null or empty groupValue comes before studyValue
    }
    if (!studyValue) {
        return 1; // Null or empty studyValue comes after groupValue
    }

	if (groupType === 'time') {
        // compare the date strings in YYYY-MM-DD format
        const dateA = new Date(groupValue).toISOString().split('T')[0]; // Format: YYYY-MM-DD
        const dateB = new Date(studyValue).toISOString().split('T')[0]; // Format: YYYY-MM-DD

        return dateA.localeCompare(dateB); // Compare the date strings
    }

	return groupValue.localeCompare(studyValue?.toLowerCase());
}
